import type { RestaurantSemData } from '@/types/sem';
import type { LandingPageReport } from '@/types/landing-page-analysis';
import { getSeverity, calcRevenueLoss } from './story-utils';
import type { SeverityLevel } from './story-utils';
import { normalizeRestaurantName } from '@/lib/normalize-names';

interface RestaurantPillTabsProps {
  restaurants: RestaurantSemData[];
  lpReport: LandingPageReport;
  selectedId: string;
  onSelect: (id: string) => void;
}

const SEVERITY_DOT: Record<SeverityLevel, string> = {
  critical: 'bg-[var(--ed-red)]',
  warning: 'bg-[var(--ed-amber)]',
  ok: 'bg-[var(--ed-green)]',
};

const SEVERITY_ORDER: Record<SeverityLevel, number> = {
  critical: 0,
  warning: 1,
  ok: 2,
};

function getRestaurantSeverity(
  restaurant: RestaurantSemData,
  lpReport: LandingPageReport,
  allRestaurants: RestaurantSemData[]
): SeverityLevel {
  const domains = restaurant.data.campaigns.flatMap(c =>
    c.adGroups?.flatMap(ag =>
      ag.ads?.map(ad => {
        try { return new URL(ad.finalUrl).hostname.replace('www.', ''); } catch { return ''; }
      }) || []
    ) || []
  ).filter(Boolean);

  const pages = lpReport.pages.filter(page => {
    if (page.campaignId && restaurant.data.campaigns.some(c => c.id === page.campaignId)) return true;
    try {
      const pageDomain = new URL(page.url).hostname.replace('www.', '');
      return domains.includes(pageDomain);
    } catch { return false; }
  });

  if (pages.length === 0) return 'ok';

  const validR = allRestaurants.filter(r => r.data.summary.clicks > 0);
  const portfolioAvgCvr = validR.length > 0
    ? validR.reduce((s, r) => s + (r.data.summary.conversions / r.data.summary.clicks) * 100, 0) / validR.length
    : 0;

  let worst: SeverityLevel = 'ok';
  for (const page of pages) {
    const sum = restaurant.data.summary;
    const pageCvr = sum.clicks > 0 ? (sum.conversions / sum.clicks) * 100 : 0;
    const avgConvValue = sum.conversions > 0 ? sum.conversionValue / sum.conversions : 0;
    const revenueLoss = calcRevenueLoss(portfolioAvgCvr / 100, pageCvr / 100, sum.clicks, avgConvValue);
    const sev = getSeverity(revenueLoss, page.scores.messageMatch);
    if (SEVERITY_ORDER[sev] < SEVERITY_ORDER[worst]) worst = sev;
    if (worst === 'critical') break;
  }
  return worst;
}

export function RestaurantPillTabs({ restaurants, lpReport, selectedId, onSelect }: RestaurantPillTabsProps) {
  const items = restaurants.map(r => ({
    id: r.id,
    name: normalizeRestaurantName(r.name),
    severity: getRestaurantSeverity(r, lpReport, restaurants),
  })).sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);

  return (
    <div className="pill-tabs-scroll">
      <div className="pill-tabs">
        {items.map(item => (
          <button
            key={item.id}
            onClick={() => onSelect(item.id)}
            className={`pill-tab ${selectedId === item.id ? 'pill-tab-active' : ''}`}
          >
            <span className={`pill-dot ${SEVERITY_DOT[item.severity]}`} />
            {item.name}
          </button>
        ))}
      </div>
    </div>
  );
}
